'use client';

import { styled } from 'styled-components';
import type { TestimonialCardProps } from '@dvd/components/TestimonialCard';
import TestimonialCard from '@dvd/components/TestimonialCard';
import type { Color } from '@types';

type G2ReviewProps = TestimonialCardProps & {
  /**
   * The author of the quote on the card
   */
  attribution: string;
  /**
   * The title of the attributing author
   */
  attributionTitle: string;
  /**
   * What href should the attributionTitle point to?
   */
  attributionSourceHref?: string;
  /**
   * The background color of the card; do not set if card is centered
   */
  backgroundColor?: Color | 'transparent';
  /**
   * The card headline
   */
  headline?: string;
  /**
   * The card quote
   */
  quote: string;
  /**
   * The number of the rating
   */
  rating?: number;
  /**
   * The color of the rating stars; yellow or blue
   */
  ratingColor?: 'blue' | 'yellow';
  /**
   * set to false if we should NOT show rating
   */
  showRating?: boolean;
  /**
   * The text color of the card
   */
  textColor?: Color;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
  /**
   * Set to false if we should NOT show G2 as source
   */
  showCite?: boolean;
  /**
   * Align start or center; default is center
   */
  alignment?: 'center' | 'start';
};

const G2ReviewLayout = styled.div<{ $alignment: string }>`
  margin-block: ${({ theme }) => `${theme.spacing[3]}`};

  .g2ReviewTestimonial {
    padding-block-end: ${({ theme }) => theme.spacing['1']};
  }

  .testimonialCard {
    text-align: ${({ $alignment }) => $alignment};
    text-wrap: balance;

    :first-child {
      /**  Center the star rating of TestimonialCard */
      justify-content: ${({ $alignment }) => $alignment};
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    display: grid;
    margin-block: ${({ theme }) => theme.spacing[4]};
    ${({ theme }) => theme.grid.columns.large};

    .g2ReviewTestimonial {
      padding-block-end: 0;
      grid-column: 3 / span 8;
    }

    .testimonialCard {
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: initial;
      padding: ${({ theme }) => theme.spacing['4']};

      span {
        /** Style the attributionTitle of TestimonialCard */
        display: initial;
      }
    }
  }
`;

const G2Review = ({
  alignment = 'center',
  attribution,
  attributionSourceHref,
  attributionTitle,
  backgroundColor,
  className,
  headline,
  quote,
  rating,
  ratingColor,
  showRating,
  textColor,
  showCite,
}: G2ReviewProps) => {
  return (
    <G2ReviewLayout $alignment={alignment}>
      <div className="g2ReviewTestimonial">
        <TestimonialCard
          attribution={attribution}
          attributionSourceHref={attributionSourceHref}
          attributionTitle={attributionTitle}
          backgroundColor={
            alignment === 'center' ? 'transparent' : backgroundColor
          }
          className={className}
          headline={headline}
          quote={quote}
          rating={rating}
          ratingColor={ratingColor}
          showCite={showCite}
          showRating={showRating}
          textColor={textColor}
        />
      </div>
    </G2ReviewLayout>
  );
};

export default G2Review;
